import { create } from 'zustand';
import api from '../api/axios';
import { normalizeTenantPlan, hasPlanFeature } from '../utils/planAccess';
import bcrypt from 'bcryptjs';
import { clearPaymentCache } from '../utils/paymentInfo';
import { getOfflineAuth, offlineLoginBlocker, syncOfflineAuth } from '../utils/offlineAuth';
import { isOffline as netIsOffline } from '../utils/netStatus';

// ── Offline giriş (internet yoxdur) ─────────────────────────────────────────────
// Cihazda saxlanan giriş paketindən (utils/offlineAuth.js) PIN/şifrə bcrypt ilə LOKAL yoxlanır.
// İstifadəçiyə ONLINE girişlə EYNİ icazələr verilir (paketdə serverin hazırladığı user obyekti var).
// Hamı /waiter/tables-ə gedir (offline yalnız sifarişlər işləyir).
const finishOfflineLogin = (set, payload, waiterToken) => {
    // Real token (əvvəlki online girişdən) qalır — sinxron üçün; yoxdursa placeholder.
    const sessToken = localStorage.getItem('pos_token') || 'offline';
    const nu = normalizeUser({ ...payload, _offlineLogin: true });
    try {
        localStorage.setItem('pos_user', JSON.stringify(nu));
        localStorage.setItem('pos_token', sessToken);
        if (waiterToken) localStorage.setItem('pos_waiter_token', waiterToken);
    } catch { /* ignore */ }
    set({ user: nu, token: sessToken, waiterToken: waiterToken || null, loading: false, error: null });
    return { ok: true, user: nu, redirect: '/waiter/tables' };
};

// Offline girişin məlumatı YALNIZ yaddaşda (localStorage-a yazılmır): internet qayıdanda eyni
// istifadəçi serverə səssiz yenidən daxil olsun (real token + online icazələr), işi kəsilmədən.
let offlineCreds = null;

const bcryptMatch = async (plain, hash) => {
    if (!plain || !hash) return false;
    try { return await bcrypt.compare(String(plain), String(hash)); } catch { return false; }
};

// null → offline giriş bu tenantda yoxdur (adi xəta göstərilsin)
const tryOfflineWaiterLogin = async (set, pin, waiterToken) => {
    const block = offlineLoginBlocker(waiterToken);
    if (block === null) return null;
    if (block) return { ok: false, msg: block };
    const bundle = getOfflineAuth(waiterToken);
    for (const e of bundle.pins || []) {
        if (await bcryptMatch(pin, e.hash)) { offlineCreds = { kind: 'pin', pin, waiterToken }; return finishOfflineLogin(set, e.user, waiterToken); }
    }
    return { ok: false, msg: 'PIN yanlışdır (offline).' };
};

// Ofisiant ekranında ŞİFRƏ ilə offline giriş (admin / kassir / administrator)
const tryOfflineWaiterPasswordLogin = async (set, password, waiterToken) => {
    const block = offlineLoginBlocker(waiterToken);
    if (block === null) return null;
    if (block) return { ok: false, msg: block };
    const bundle = getOfflineAuth(waiterToken);
    for (const e of bundle.passwords || []) {
        if (await bcryptMatch(password, e.hash)) { offlineCreds = { kind: 'wpass', password, waiterToken }; return finishOfflineLogin(set, e.user, waiterToken); }
    }
    return { ok: false, msg: 'Şifrə yanlışdır (offline).' };
};

// Admin tabı (istifadəçi adı + şifrə) offline
const tryOfflineAdminLogin = async (set, username, password) => {
    let waiterToken = ''; try { waiterToken = localStorage.getItem('pos_waiter_token') || ''; } catch { /* ignore */ }
    const block = offlineLoginBlocker('');
    if (block === null) return null;
    if (block) return { ok: false, msg: block };
    const bundle = getOfflineAuth('');
    const uname = String(username || '').toLowerCase().trim();
    const cands = (bundle.passwords || []).filter((e) => (e.usernames || []).includes(uname));
    if (!cands.length) return { ok: false, msg: 'İstifadəçi tapılmadı (offline).' };
    for (const e of cands) {
        if (await bcryptMatch(password, e.hash)) { offlineCreds = { kind: 'admin', username, password }; return finishOfflineLogin(set, e.user, waiterToken || bundle.waiter_token || ''); }
    }
    return { ok: false, msg: 'Şifrə yanlışdır (offline).' };
};

const normalizeUser = (user) => (
    user
        ? {
            ...user,
            tenant_plan: normalizeTenantPlan(user.tenant_plan)
        }
        : null
);

// Token + user-i saxla, state yenilə (OTP-dən sonra və ya birbaşa login)
// Offline lisenziya güzəşti — online lisenziya OK olanda imzalı "bu gün keçib" tokenini saxla.
// ADDITIVE: online davranışa təsir etmir; yalnız gələcək offline yoxlaması üçün saxlanır.
const refreshLicenseGrace = () => {
    try {
        api.get('/auth/license-grace').then(({ data }) => {
            if (data && data.ok && data.token) {
                localStorage.setItem('pos_license_grace', JSON.stringify({
                    token: data.token, date: data.date, offline: !!data.offline_mode_enabled,
                    settings: data.settings || null, // offline hesab cəmi üçün servis ayarları
                }));
            }
        }).catch(() => { /* şəbəkə/xəta — mövcud token qalır */ });
    } catch { /* ignore */ }
};

const storeSession = (set, token, user, waiterToken) => {
    const nu = normalizeUser(user);
    localStorage.setItem('pos_token', token);
    localStorage.setItem('pos_user', JSON.stringify(nu));
    if (waiterToken) localStorage.setItem('pos_waiter_token', waiterToken);
    else localStorage.removeItem('pos_waiter_token');
    set({ user: nu, token, waiterToken: waiterToken || null, loading: false });
    refreshLicenseGrace(); // fire-and-forget
    // Offline giriş paketi (bütün işçilər) — token yazıldıqdan sonra arxa fonda
    setTimeout(() => { syncOfflineAuth(); }, 1500);
    return nu;
};

const roleRedirect = (user, isWaiter) => {
    if (!isWaiter) {
        if (user.role === 'superadmin') return '/superadmin/dashboard';
        if (user.role === 'anbardar') return hasPlanFeature(user.tenant_plan, 'warehouse') ? '/warehouse/dashboard' : '/admin/menu';
        return '/admin/dashboard';
    }
    if (user.role === 'anbardar') return hasPlanFeature(user.tenant_plan, 'warehouse') ? '/warehouse/dashboard' : '/admin/menu';
    return '/waiter/tables';
};

const useAuthStore = create((set, get) => ({
    user: normalizeUser(JSON.parse(localStorage.getItem('pos_user')) || null),
    token: localStorage.getItem('pos_token') || null,
    waiterToken: localStorage.getItem('pos_waiter_token') || null,
    loading: false,
    error: null,

    adminLogin: async (username, password) => {
        set({ loading: true, error: null });

        try {
            // Superadmin üçün etibarlı cihaz (30 gün OTP-siz) — Windows/masaüstü daxil.
            // Token yalnız superadmin scope-una uyğun gəlir, tenant girişini keçirmir.
            let deviceTrust = null;
            try { deviceTrust = localStorage.getItem('pos_sa_device_trust') || null; } catch { /* ignore */ }
            const res = await api.post('/auth/admin-login', { username, password, device_trust: deviceTrust });
            // WhatsApp OTP tələb olunursa — token yox, challenge qaytar
            if (res.data.otp_required) {
                set({ loading: false });
                return { ok: true, otp_required: true, challenge_id: res.data.challenge_id, to_masked: res.data.to_masked, scope: res.data.scope };
            }
            const user = storeSession(set, res.data.token, res.data.user, null);

            // Backend redirect verirsə (məs. mətbəx hesabı → /kitchen) onu işlət
            let redirect = res.data.redirect || roleRedirect(user, false);
            return { ok: true, user, redirect };
        } catch (err) {
            // Şəbəkə xətası + offline → keşlənmiş logins-dən parolu lokal yoxla
            if (!err.response) {
                const off = await tryOfflineAdminLogin(set, username, password);
                if (off) return off;
            }
            const msg = err.response?.data?.msg || 'Xəta baş verdi.';
            set({ loading: false, error: msg });
            // Lisenziya bitib + parol düzgün → ödəniş ekranı bileti (panelə giriş vermir)
            if (err.response?.data?.code === 'license_expired' && err.response?.data?.pay_ticket) {
                return { ok: false, msg, code: 'license_expired', pay_ticket: err.response.data.pay_ticket };
            }
            return { ok: false, msg };
        }
    },

    waiterLogin: async (pin, waiterToken) => {
        set({ loading: true, error: null });

        try {
            const res = await api.post('/auth/waiter-login', {
                pin,
                token: waiterToken
            });
            // Admin PIN → OTP tələb oluna bilər (waiterToken challenge-a qoşulur)
            if (res.data.otp_required) {
                set({ loading: false });
                return { ok: true, otp_required: true, challenge_id: res.data.challenge_id, to_masked: res.data.to_masked, waiterToken };
            }
            const user = storeSession(set, res.data.token, res.data.user, waiterToken);
            return { ok: true, user, redirect: roleRedirect(user, true) };
        } catch (err) {
            // Şəbəkə xətası (internet yox) + offline rejim → PIN-i keşdən lokal yoxla
            if (!err.response) {
                const off = await tryOfflineWaiterLogin(set, pin, waiterToken);
                if (off) return off;
            }
            const msg = err.response?.data?.msg || 'PIN yanlışdır.';
            set({ loading: false, error: msg });
            return { ok: false, msg };
        }
    },

    // İnternet qayıtdı + offline giriş edilib → eyni məlumatla səssiz online giriş (real token).
    // Alınmasa (PIN dəyişib, OTP tələb olunur, proqram yenidən açılıb) → təmiz çıxış (PIN ekranı),
    // başqa istifadəçinin köhnə tokeni ilə işləməsin.
    upgradeOfflineSession: async () => {
        const u = get().user;
        if (!u || !u._offlineLogin) return;
        const c = offlineCreds;
        let r = null;
        try {
            if (c?.kind === 'pin') r = await get().waiterLogin(c.pin, c.waiterToken);
            else if (c?.kind === 'wpass') r = await get().waiterPasswordLogin(c.password, c.waiterToken);
            else if (c?.kind === 'admin') r = await get().adminLogin(c.username, c.password);
        } catch { r = null; }
        const nu = get().user;
        if (r?.ok && !r.otp_required && nu && !nu._offlineLogin) { offlineCreds = null; return; } // uğurlu
        if (r?.ok && nu?._offlineLogin) return; // şəbəkə yenə getdi (offline girişə düşdü) → sonra yenə
        offlineCreds = null;
        get().logout();
    },

    // Ofisiant ekranında ŞİFRƏ ilə giriş — admin/kassir/administrator waiter sisteminə düşür.
    waiterPasswordLogin: async (password, waiterToken) => {
        set({ loading: true, error: null });
        try {
            const res = await api.post('/auth/waiter-login-password', {
                password,
                token: waiterToken
            });
            const token = res.data.token;
            const user = normalizeUser(res.data.user);

            localStorage.setItem('pos_token', token);
            localStorage.setItem('pos_user', JSON.stringify(user));
            localStorage.setItem('pos_waiter_token', waiterToken);

            set({ user, token, waiterToken, loading: false });
            refreshLicenseGrace();
            setTimeout(() => { syncOfflineAuth(); }, 1500); // offline giriş paketi
            return { ok: true, user, redirect: '/waiter/tables' };
        } catch (err) {
            // Şəbəkə xətası + offline rejim → şifrəni cihazdakı paketdən yoxla
            if (!err.response) {
                const off = await tryOfflineWaiterPasswordLogin(set, password, waiterToken);
                if (off) return off;
            }
            const msg = err.response?.data?.msg || 'Şifrə yanlışdır.';
            set({ loading: false, error: msg });
            return { ok: false, msg };
        }
    },

    // Superadmin recovery kod ilə giriş (WhatsApp itsə) — OTP-siz.
    superadminRecovery: async (username, password, recoveryCode) => {
        set({ loading: true, error: null });
        try {
            const res = await api.post('/auth/superadmin-recovery', {
                username, password, recovery_code: recoveryCode,
            });
            const user = storeSession(set, res.data.token, res.data.user, null);
            return { ok: true, user, redirect: '/superadmin/dashboard' };
        } catch (err) {
            const msg = err.response?.data?.msg || 'Recovery giriş alınmadı.';
            set({ loading: false, error: msg });
            return { ok: false, msg };
        }
    },

    // OTP kodunu təsdiqlə → token al, sessiyanı qur. waiterToken varsa ofisiant girişi.
    // rememberDevice=true (yalnız superadmin) → cihaz 30 gün etibarlı, təkrar OTP istəmir.
    verifyOtp: async (challengeId, code, waiterToken = null, rememberDevice = false) => {
        set({ loading: true, error: null });
        try {
            const res = await api.post('/auth/verify-otp', {
                challenge_id: challengeId, code,
                ...(rememberDevice ? { remember_device: true, device_label: 'Superadmin cihazı' } : {}),
            });
            // Superadmin: qayıdan trust token-i saxla (30 gün OTP-siz giriş, Windows daxil)
            if (rememberDevice && res.data.device_trust) {
                try { localStorage.setItem('pos_sa_device_trust', res.data.device_trust); } catch { /* ignore */ }
            }
            const user = storeSession(set, res.data.token, res.data.user, waiterToken || null);
            return { ok: true, user, redirect: roleRedirect(user, !!waiterToken) };
        } catch (err) {
            const msg = err.response?.data?.msg || 'Kod yanlışdır.';
            set({ loading: false, error: msg });
            return { ok: false, msg, blocked: !!err.response?.data?.blocked, remaining: err.response?.data?.remaining };
        }
    },

    // Çıxış.
    //   • Nativ app (iOS/Android) içindəyiksə → app-a SİQNAL göndər ki, app öz nativ
    //     login (PIN/parol) ekranını göstərsin (web login YOX). App bridge inject edir.
    //   • Web / PC / mobil-web → mövcud tam naviqasiya (dəyişmir).
    logout: () => {
        clearPaymentCache();
        const waiterToken = get().waiterToken || localStorage.getItem('pos_waiter_token');
        api.post('/auth/logout').catch(() => {});

        localStorage.removeItem('pos_token');
        localStorage.removeItem('pos_user');

        // OFFLINE: tam səhifə yükləməsi internetsiz alınmır (C# öz «internet yoxdur» ekranını açır).
        // Tətbiqin İÇİNDƏ PIN ekranına keç (react-router popstate-i dinləyir) — offline PIN girişi işləsin.
        try {
            if (netIsOffline() && !window.webkit?.messageHandlers?.fiscalLogout && !window.FiscalAndroid) {
                set({ user: null, token: null, loading: false, error: null });
                // Ofisiant ekranı tokeni itməsin (yoxsa PIN yerinə admin girişi açılır): offline paketdən də götür
                let wt = waiterToken;
                if (!wt) { try { wt = (JSON.parse(localStorage.getItem('pos_offline_auth') || 'null') || {}).waiter_token || ''; } catch { wt = ''; } }
                if (wt) { try { localStorage.setItem('pos_waiter_token', wt); } catch { /* ignore */ } }
                const url = wt ? `/?t=${wt}` : '/';
                window.history.pushState({}, '', url);
                window.dispatchEvent(new PopStateEvent('popstate'));
                return;
            }
        } catch { /* adi çıxışa düş */ }

        // ── Nativ app siqnalı (varsa) ──
        try {
            // iOS WKWebView — app `fiscalLogout` adlı WKScriptMessageHandler qeyd edir
            if (window.webkit?.messageHandlers?.fiscalLogout) {
                localStorage.removeItem('pos_waiter_token');
                window.webkit.messageHandlers.fiscalLogout.postMessage('logout');
                return; // web naviqasiya etmə — app nativ login göstərəcək
            }
            // Android WebView — app `FiscalAndroid` JS interface əlavə edir
            if (window.FiscalAndroid && typeof window.FiscalAndroid.onLogout === 'function') {
                localStorage.removeItem('pos_waiter_token');
                window.FiscalAndroid.onLogout();
                return;
            }
        } catch { /* bridge yoxdursa adi web çıxışına düş */ }

        // ── Web / PC / mobil-web (bridge yoxdur) ──
        if (waiterToken) {
            window.location.href = `/?t=${waiterToken}`;
            return;
        }
        localStorage.removeItem('pos_waiter_token');
        window.location.href = '/';
    },

    clearError: () => set({ error: null }),

    // Superadmin → tenant admin panelinə keçid (impersonation).
    // Cari superadmin sessiyası saxlanılır ki, geri qayıtmaq olsun.
    impersonate: (token, user) => {
        const cur = get();
        if (cur.user?.role === 'superadmin' && cur.token) {
            localStorage.setItem('pos_super_token', cur.token);
            localStorage.setItem('pos_super_user', JSON.stringify(cur.user));
        }
        const nu = normalizeUser(user);
        localStorage.setItem('pos_token', token);
        localStorage.setItem('pos_user', JSON.stringify(nu));
        localStorage.removeItem('pos_waiter_token');
        set({ user: nu, token, waiterToken: null });
    },

    // Impersonation-dan superadmin sessiyasına qayıt
    exitImpersonation: () => {
        const st = localStorage.getItem('pos_super_token');
        const su = localStorage.getItem('pos_super_user');
        if (!st || !su) return false;
        localStorage.setItem('pos_token', st);
        localStorage.setItem('pos_user', su);
        localStorage.removeItem('pos_super_token');
        localStorage.removeItem('pos_super_user');
        set({ token: st, user: normalizeUser(JSON.parse(su)) });
        return true;
    },

    isImpersonating: () => !!localStorage.getItem('pos_super_token'),

    // Etibarlı token-dən təzə token al → localStorage + state yenilə.
    // Token heç vaxt iş günü ərzində bitməsin deyə dövri çağırılır (App-da).
    refreshToken: async () => {
        const token = get().token || localStorage.getItem('pos_token');
        if (!token) return false;
        try {
            const res = await api.post('/auth/refresh');
            const newToken = res.data?.token;
            if (newToken) {
                localStorage.setItem('pos_token', newToken);
                set({ token: newToken });
                return true;
            }
        } catch { /* bitib/şəbəkə — interceptor lazımsa çıxış edəcək */ }
        return false;
    },

    // Nativ app (iOS/Android) `https://.../?token=<JWT>` ilə açanda autentifikasiyanı
    // qurur: token-i saxlayır, /auth/me ilə user-i çəkir, URL-dən token-i təmizləyir.
    // YALNIZ URL-də `token` varsa işləyir → mövcud login axınlarına (web/C#/waiter ?t=)
    // HEÇ TOXUNMUR. Qaytarır: handoff baş verdimi (true/false).
    bootstrapFromUrlToken: async () => {
        let t = null, wt = null;
        try {
            const sp = new URLSearchParams(window.location.search);
            t = sp.get('token');
            wt = sp.get('t'); // waiter sessionUrl-da tenant token da gələ bilər (logout → PIN)
        } catch { t = null; }
        if (!t) return false;
        localStorage.setItem('pos_token', t);
        if (wt) localStorage.setItem('pos_waiter_token', wt);
        let okUser = null;
        try {
            const res = await api.get('/auth/me');     // interceptor yeni token-i işlədir
            okUser = normalizeUser(res.data.user);
            localStorage.setItem('pos_user', JSON.stringify(okUser));
            set({ token: t, user: okUser });
        } catch {
            // Token keçərsizdirsə təmizlə → normal login axınına düşsün
            localStorage.removeItem('pos_token');
            localStorage.removeItem('pos_user');
        } finally {
            // VACİB: birbaşa rola uyğun dashboard-a yönləndir (URL-i dəyiş).
            // `/`-ə qalsaq Login komponenti mount olub sessiyanı SİLİR → PIN qayıdır.
            // Dashboard route-una getsək Login ümumiyyətlə mount olmur.
            try {
                let dest = '/'; // token keçərsizdirsə normal login
                if (okUser) {
                    const r = okUser.role;
                    dest = r === 'superadmin' ? '/superadmin/dashboard'
                         : r === 'admin'      ? '/admin/dashboard'
                         : r === 'anbardar'   ? '/warehouse/dashboard'
                         : '/waiter/tables';
                }
                window.history.replaceState({}, '', dest);
            } catch { /* ignore */ }
        }
        return true;
    }
}));

export default useAuthStore;
